<template>
  <div>
    <!-- Só a sub-aba ativa fica montada: cada módulo busca os próprios dados ao abrir. -->
    <TabelaValidadores
      v-if="subAba === 'cadastro'"
      ref="tabela"
      :itens="linhas"
      :loading="loading"
      :token="token"
      :mostrar-inativos="mostrarInativos"
      :permissoes="permissoes"
      :headers-csv="headersCsv"
      :help-csv="helpCsv"
      v-on="repasse"
    />
    <EmbarquePermitido v-else-if="subAba === 'embarque'" :key="chaveModulo" :equipamentos="equipamentosSuite"
      :apenas-da-lista="recortado" :token="token" />
    <Diagnostico v-else-if="subAba === 'diagnostico'" :key="chaveModulo" :equipamentos="equipamentosSuite"
      :apenas-da-lista="recortado" :token="token" />
    <template v-else-if="subAba === 'erros'">
      <!-- A ajuda da sub-aba acompanha a fonte escolhida aqui (ver subAbas). -->
      <v-btn-toggle v-if="temIsapi && temHikcentral" v-model="fonteErros" mandatory dense color="primary" class="mb-2">
        <v-btn value="isapi" small class="text-none">{{ $t('equipment.panel.errorSources.isapi') }}</v-btn>
        <v-btn value="hikcentral" small class="text-none">{{ $t('equipment.panel.errorSources.hikcentral') }}</v-btn>
      </v-btn-toggle>
      <ErrosSync v-if="fonteErrosEfetiva === 'isapi'" :key="`isapi-${chaveModulo}`" :equipamentos="equipamentosSuite"
        :apenas-da-lista="recortado" :token="token" :pode-reaplicar="permissoes.reprocessar" @refresh="$emit('refresh')" />
      <ErrosHikcentral v-else :key="`hik-${chaveModulo}`" :equipamentos="equipamentosSuite"
        :apenas-da-lista="recortado" :token="token" :pode-reaplicar="permissoes.reaplicarHikcentral" />
    </template>
    <RelatorioAtualizacoes v-else-if="subAba === 'atualizacoes'" :key="chaveModulo" :equipamentos="equipamentosSuite"
      :apenas-da-lista="recortado" :token="token" />
    <FirmwaresPermitidos v-else-if="subAba === 'firmwares'" embedded />
  </div>
</template>

<script>
import TabelaValidadores from './TabelaValidadores.vue';
import EmbarquePermitido from './EmbarquePermitido.vue';
import Diagnostico from './Diagnostico.vue';
import ErrosSync from './ErrosSync.vue';
import ErrosHikcentral from './ErrosHikcentral.vue';
import RelatorioAtualizacoes from './RelatorioAtualizacoes.vue';
import FirmwaresPermitidos from './FirmwaresPermitidos.vue';
import { falaComSuite } from '../equipamentosClasses';

const EVENTOS_CADASTRO = ['novo', 'editar', 'excluir', 'status', 'bulk-edit', 'qr-download', 'saveBulk', 'refresh', 'update:mostrarInativos'];

/**
 * Aba Validadores da tela de Equipamentos: o cadastro dos leitores com o estado de envio e,
 * conforme as suites em uso, os módulos que eram de Status dos Dispositivos e Hikvision.
 * Leitor sem suite (cliente sem Hikvision) é só cadastro: as sub-abas de suite somem.
 *
 * A barra das sub-abas fica no card do topo da tela, junto do filtro e das abas: o painel
 * informa a lista (evento `sub-abas`) e recebe a escolhida (`subAba`, .sync).
 */
export default {
  name: 'PainelValidadores',
  components: {
    TabelaValidadores,
    EmbarquePermitido,
    Diagnostico,
    ErrosSync,
    ErrosHikcentral,
    RelatorioAtualizacoes,
    FirmwaresPermitidos,
  },
  props: {
    // Equipamentos da classe Validador, já no recorte de veículo da tela.
    validadores: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    token: { type: String, default: null },
    mostrarInativos: { type: Boolean, default: false },
    permissoes: { type: Object, default: () => ({}) },
    headersCsv: { type: Array, default: () => [] },
    helpCsv: { type: Array, default: () => [] },
    // Filtro de veículo ativo na tela: os módulos mostram só os leitores da lista.
    idVeiculo: { type: [Number, String], default: null },
    // ISAPI ligado na instalação: a lista de firmwares vale mesmo antes do primeiro leitor.
    isapiNaInstalacao: { type: Boolean, default: false },
    subAba: { type: String, default: 'cadastro' },
  },
  data: () => ({
    fonteErros: 'isapi',
  }),
  computed: {
    repasse() {
      const ouvintes = {};
      EVENTOS_CADASTRO.forEach((evento) => {
        ouvintes[evento] = (...args) => this.$emit(evento, ...args);
      });
      return ouvintes;
    },
    recortado() {
      return this.idVeiculo != null;
    },
    chaveModulo() {
      return `${this.idVeiculo || 'frota'}`;
    },
    // Linha do cadastro com o que a suite do leitor fornece. Inventário só da suite atual: o
    // cache gravado por outra (leitor que trocou de suite) seria lido com o rótulo errado.
    linhas() {
      return this.validadores.map((e) => {
        const suite = falaComSuite(e);
        const inventaria = suite && e.suite_suporta_inventario == 1;
        const inventarioAtual = inventaria && e.inventario_suite === e.suite_efetiva;
        return {
          ...e,
          fala_com_suite: suite,
          suporta_inventario: inventaria,
          suporta_aplicar_escala: suite && e.suite_suporta_aplicar_escala == 1,
          fonte_inventario: inventaria ? e.suite_fonte_inventario : null,
          qtd_pessoas: inventarioAtual ? e.inventario_qtd_pessoas : null,
          qtd_cartoes: inventarioAtual ? e.inventario_qtd_cartoes : null,
        };
      });
    },
    // Formato que os módulos de suite esperam (o mesmo de isapi/getisapiequipamentos).
    equipamentosSuite() {
      return this.linhas
        .filter((e) => e.fala_com_suite)
        .map((e) => ({
          id_equipamento: e.id_equipamento,
          nome_equipamento: e.nome_equipamento,
          cod_equipamento: e.cod_equipamento,
          id_veiculo: e.id_veiculo,
          cod_veiculo: e.cod_veiculo,
          suite_efetiva: e.suite_efetiva,
          nome_suite: e.nome_suite_efetiva,
          suporta_inventario: e.suporta_inventario ? 1 : 0,
          fonte_inventario: e.fonte_inventario,
        }));
    },
    temInventario() {
      return this.equipamentosSuite.some((e) => e.suporta_inventario == 1);
    },
    temIsapi() {
      return this.equipamentosSuite.some((e) => e.suite_efetiva === 'ISAPI');
    },
    temHikcentral() {
      return this.equipamentosSuite.some((e) => e.suite_efetiva === 'HIKCENTRAL');
    },
    fonteErrosEfetiva() {
      if (this.temIsapi && this.temHikcentral) return this.fonteErros;
      return this.temIsapi ? 'isapi' : 'hikcentral';
    },
    errosPendentes() {
      return this.linhas.reduce((total, e) => total + (e.fala_com_suite
        ? (e.qtd_erros_pendentes || 0) + (e.qtd_erros_esgotados || 0) + (e.qtd_erros_hikcentral || 0)
        : 0), 0);
    },
    subAbas() {
      const t = (k) => this.$t(`equipment.panel.subtabs.${k}`);
      const ajuda = (k) => this.$t(`equipment.help.subtabs.${k}`);
      const firmwares = { key: 'firmwares', label: t('firmwares'), icon: 'mdi-chip', help: ajuda('firmwares') };
      const abas = [{ key: 'cadastro', label: t('devices'), icon: 'mdi-devices', help: ajuda('cadastro') }];
      if (!this.equipamentosSuite.length) {
        if (this.isapiNaInstalacao && this.permissoes.firmwares) abas.push(firmwares);
        return abas;
      }
      if (this.temInventario) {
        abas.push({ key: 'embarque', label: t('boarding'), icon: 'mdi-account-check', help: ajuda('embarque') });
        abas.push({ key: 'diagnostico', label: t('diagnostics'), icon: 'mdi-stethoscope', help: ajuda('diagnostico') });
      }
      if (this.temIsapi || this.temHikcentral) {
        abas.push({ key: 'erros', label: t('errors'), icon: 'mdi-sync-alert', badge: this.errosPendentes || null,
          help: ajuda(`errors_${this.fonteErrosEfetiva}`) });
      }
      abas.push({ key: 'atualizacoes', label: t('updates'), icon: 'mdi-update', help: ajuda('atualizacoes') });
      if ((this.temIsapi || this.isapiNaInstalacao) && this.permissoes.firmwares) abas.push(firmwares);
      return abas;
    },
  },
  watch: {
    subAbas: {
      immediate: true,
      handler(lista) {
        this.$emit('sub-abas', lista);
        // Recorte que tira a suite da lista não deixa a tela presa numa sub-aba que sumiu.
        if (!lista.some((a) => a.key === this.subAba)) this.$emit('update:subAba', 'cadastro');
      },
    },
  },
  methods: {
    limpaSelecao() {
      if (this.$refs.tabela) this.$refs.tabela.limpaSelecao();
    },
  },
};
</script>
